// @givmo/compliance — the "Use AI on your organization’s data" card (CCM-R1-AI-SETTING; D-CCM-21
// set C Q2, Q3, A-C3), for a host's Settings page: Org Machinery OM-17 seam B, a module-owned panel
// over the module's own operations.
//
// ══ THE APPROVED FRAME, VERBATIM ═══════════════════════════════════════════════════════════════
//
// Frame 11 of the approved slice-1b renders (2026-09-29): the title, the approved sentence, and the
// switch on the title row with its state written beside it, "On" or "Off". Nothing else here is new
// copy: the saving status is the Settings page's own ("Saving…", "Saved."), and a change that did
// not save says the Settings page's own line for a single choice that did not save — the words of
// `@givmo/org-console`'s `APPROVAL_SAVE_FAILED_LINE`. This package imports no org-console module,
// so the words are repeated here and the charity host's test holds the two equal. A change refused
// as made from a stale view says the server's own line instead (below).
//
// ══ WHO SEES IT ════════════════════════════════════════════════════════════════════════════════
//
// A host mounts it only for a holder of the module's Owner string (`COMPLIANCE_OWNER_PERMISSION`:
// a Compliance Owner or a Global Admin). That is the seam's law, absence and never a disabled
// control: a Member meets the setting where it matters, in the dialog `CCM-R1-SUBMIT` builds, not
// here. Like every Compliance surface it asks the server nothing until the host's standing is
// `'available'`, and it is absent while the module is dark on the backend (the 404).
//
// ══ AUTOSAVE WITH ROLLBACK ═════════════════════════════════════════════════════════════════════
//
// A single choice saves on the flip (the seam's law). While the change is in flight the switch shows
// the new value and holds still, and the server's answer then replaces it. A change that fails is
// dropped, so the switch returns to the value in force, and the card reads the setting again, so a
// change someone else made meanwhile (the server's stale refusal) is what the switch then shows.
// The change counts as unsaved for the page's guard only while it is in flight.
//
// ══ THE FAILURE LINE IS ONLY EVER TRUE ═════════════════════════════════════════════════════════
//
// The line is said once that re-read has answered, and only if the record still does not say what
// the person asked for. Two Owners flipping the same way moments apart is the case it exists for:
// the second is refused as stale, yet the record already says exactly what they asked, so a line
// asking them to make their change again would mislead, and making it would undo it. WHEN the line
// is said keys on the record, not on the refusal's code, so a change that landed but whose answer
// was lost is not called a failure either; a change the record does not show still says the line.
//
// ══ A STALE CHANGE SAYS THE SERVER'S LINE (D-CCM-44) ═══════════════════════════════════════════
//
// WHICH line is said keys on the refusal's code. A change made from a stale view is refused with a
// 409 and `COMPLIANCE_AI_SETTING_STALE` (`isAiSettingStale`), and the card says the line the server
// serves with it, as the sign-in check card below does with its own: D-CCM-44 gave this card that
// card's words for the same situation. Each card says what its route serves, and the web keeps no
// copy of either line. The backend holds the words twice, each pinned by its own test: `_STALE` in
// `charity_compliance_ai_setting_service.py` and `_SETTING_STALE` in
// `charity_compliance_signing_service.py`. The route always serves that line as the refusal's
// `detail`; an empty one would fall back to the envelope's title (`GivmoError.message`). Any other
// failure says the Settings page's line.
//
// ══ WHO LAST CHANGED IT, AND HOW (D-CCM-96) ════════════════════════════════════════════════════
//
// Frames A1a and A1b: a line under the sentence says who last changed the setting, when, and
// whether it came through their connected AI (`aiSettingChangeLine`). The person is named from the
// host's roster (`personName`); never changed, no line.
//
// DATA CONTRACT: the `compliance-ai-setting` test-id family.

import { useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, ErrorMessage, Skeleton, Switch, testIdProp } from '@givmo/design-system';
import {
  aiSettingChangeLine,
  complianceAiSettingQueryKey,
  decodeComplianceAiSetting,
  isAiSettingStale,
} from './compliance-ai-setting';
import { useCompliance } from './compliance-context';
import { displayLocalDate, isModuleUnavailable, serverMessage } from './compliance-decode';

const PREFIX = 'compliance-ai-setting';

/**
 * The Settings page's line for a single choice that did not save: `APPROVAL_SAVE_FAILED_LINE` in
 * `@givmo/org-console`, word for word (the charity host's test holds the two equal).
 */
const CHANGE_FAILED_LINE = 'We couldn’t save that. Nothing was changed — please try again.';

export interface ComplianceAiSettingCardProps {
  /** Whether a change is in flight, for the host page's unsaved-changes guard. */
  onDirtyChange?: (dirty: boolean) => void;
}

interface AiSettingChange {
  enabled: boolean;
  /** The revision the change is made from. */
  from: number;
}

export function ComplianceAiSettingCard({ onDirtyChange }: ComplianceAiSettingCardProps) {
  const { orgRef, isReady, operations, moduleStanding, personName } = useCompliance();
  const headingId = useId();
  const queryClient = useQueryClient();
  const available = isReady && moduleStanding === 'available' && orgRef !== null;
  const queryKey = complianceAiSettingQueryKey(orgRef);
  const read = useQuery({
    queryKey,
    queryFn: () => operations.getAiSetting(orgRef ?? ''),
    enabled: available,
  });
  const [saved, setSaved] = useState(false);
  const change = useMutation({
    mutationFn: (next: AiSettingChange) =>
      operations.setAiSetting(orgRef ?? '', {
        givmo_ai_enabled: next.enabled,
        expected_revision: next.from,
      }),
    onSuccess: (answer) => {
      if (decodeComplianceAiSetting(answer) === null) {
        void queryClient.invalidateQueries({ queryKey });
      } else {
        queryClient.setQueryData(queryKey, answer);
      }
      setSaved(true);
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
  const inFlight = change.isPending;
  useEffect(() => {
    onDirtyChange?.(inFlight);
  }, [inFlight, onDirtyChange]);

  if (!available) return null;
  if (read.error != null && isModuleUnavailable(read.error)) return null;

  const setting = decodeComplianceAiSetting(read.data);
  const shown = inFlight ? change.variables?.enabled : setting?.givmoAiEnabled;
  const unreadable = !read.isPending && setting === null;
  // Said only once the re-read has answered, and only while the record does not say what was asked.
  const changeFailed =
    change.isError &&
    !read.isFetching &&
    !(setting !== null && setting.givmoAiEnabled === change.variables?.enabled);
  // A stale change says the server's own line (D-CCM-44), as the sign-in check card does.
  const failureLine = isAiSettingStale(change.error)
    ? serverMessage(change.error, CHANGE_FAILED_LINE)
    : CHANGE_FAILED_LINE;
  const lastChange =
    setting !== null && setting.changedAt !== null
      ? aiSettingChangeLine(
          setting,
          setting.changedByUserId !== null ? personName?.(setting.changedByUserId) : undefined,
          displayLocalDate(setting.changedAt),
        )
      : undefined;

  return (
    <Card testId={PREFIX}>
      <div className="flex flex-wrap items-start justify-between gap-x-24 gap-y-12 px-20 py-16">
        <div className="min-w-0 flex-1 basis-[320px]">
          <h3 id={headingId} className="text-s16 font-w600 text-text-strong">
            Use AI on your organization’s data
          </h3>
          <p className="mt-2 max-w-[640px] text-s13 leading-h15 text-balance text-text-tertiary">
            When this is off, Givmo never reads your documents with AI or drafts anything with AI,
            and the Givmo filing desk can’t prepare or file for you. Everything you add yourself
            works the same.
          </p>
          {lastChange !== undefined && (
            <p
              className="mt-6 text-s12 leading-h15 text-text-tertiary"
              {...testIdProp(`${PREFIX}-last-change`)}
            >
              {lastChange}
            </p>
          )}
          {changeFailed && <ErrorMessage testId={`${PREFIX}-error`}>{failureLine}</ErrorMessage>}
          {unreadable && (
            <ErrorMessage testId={`${PREFIX}-read-error`}>
              {serverMessage(read.error, 'Please try again.')}
            </ErrorMessage>
          )}
        </div>
        {shown !== undefined ? (
          <div className="flex shrink-0 items-center gap-10 pt-2">
            <span
              className="text-s13 font-w500 text-text-secondary"
              {...testIdProp(`${PREFIX}-state`)}
            >
              {shown ? 'On' : 'Off'}
            </span>
            <Switch
              appearance="saas"
              checked={shown}
              disabled={inFlight || setting === null}
              onCheckedChange={(next) => {
                if (setting === null || next === setting.givmoAiEnabled) return;
                setSaved(false);
                change.mutate({ enabled: next, from: setting.revision });
              }}
              aria-labelledby={headingId}
              testId={`${PREFIX}-switch`}
            />
          </div>
        ) : (
          read.isPending && (
            <Skeleton className="mt-2 h-20 w-[72px] shrink-0" testId={`${PREFIX}-loading`} />
          )
        )}
      </div>
      <p role="status" aria-live="polite" className="sr-only" {...testIdProp(`${PREFIX}-status`)}>
        {inFlight ? 'Saving…' : saved ? 'Saved.' : ''}
      </p>
    </Card>
  );
}
